/**
 * Pure policy for client-side image compression: byte budgets, the
 * dimension/quality step ladder, aspect-preserving fit math, and base64
 * data-URL inflation estimates. The canvas-touching encoder that consumes
 * this policy lives in `src/lib/imageCompression.ts` (browser APIs are
 * banned here — this module is Convex-bundle-safe). Consumers: notebook
 * image inserts today; the chat inline-base64 path can adopt the same
 * policy against `MEDIA_CONSTRAINTS.inlineBase64ThresholdBytes`.
 */

/**
 * Encoded-output budget (data URL bytes) for images embedded in notebook
 * notes. Pinned to the vault's 1 MiB note cap (`MAX_NOTE_SIZE` in
 * `convex/notebookActions.ts`, spec §4.1): 256 KiB = cap / 4, leaving
 * ~3-image headroom per note plus prose. A source-pin test asserts the
 * convex cap hasn't drifted from this ratio.
 */
export const NOTEBOOK_IMAGE_BUDGET_BYTES = 256 * 1024;

/**
 * Client-side mirror of the convex `MAX_NOTE_SIZE` cap (same source pin) —
 * the pre-save guard checks the UTF-8 encoded body against this instead of
 * round-tripping a 413 (re-land spec D6).
 */
export const NOTEBOOK_MAX_NOTE_BYTES = 4 * NOTEBOOK_IMAGE_BUDGET_BYTES;

export const IMAGE_COMPRESSION = {
  /** Default encoded-output budget (data URL bytes) for callers without
   *  their own ceiling; notebook embeds pass
   *  `NOTEBOOK_IMAGE_BUDGET_BYTES` explicitly. */
  defaultBudgetBytes: 2 * 1024 * 1024,
  /** Preferred output encoding; encoders fall back to JPEG when the
   *  platform can't emit WebP. */
  outputType: "image/webp",
  fallbackOutputType: "image/jpeg",
} as const;

/** Descending-fidelity encode attempts. The encoder walks the ladder and
 *  returns the first result under budget; the tail step (800px @ 0.5) lands
 *  far below any sane budget, so exhaustion means the image is undecodable
 *  rather than merely large. Order MUST stay strictly non-increasing in
 *  both fields (pinned by test). */
export const IMAGE_COMPRESSION_STEPS: ReadonlyArray<{
  maxDimensionPx: number;
  quality: number;
}> = [
  { maxDimensionPx: 2048, quality: 0.82 },
  { maxDimensionPx: 2048, quality: 0.65 },
  { maxDimensionPx: 1600, quality: 0.6 },
  { maxDimensionPx: 1200, quality: 0.55 },
  { maxDimensionPx: 800, quality: 0.5 },
];

/** Estimated size of a blob once embedded as a base64 data URL — the number
 *  the budget applies to, NOT the raw file size (base64 inflates 4/3, plus
 *  the `data:<mime>;base64,` prefix). */
export function estimateDataUrlBytes(blobBytes: number, mimeType: string): number {
  return Math.ceil(blobBytes / 3) * 4 + `data:${mimeType};base64,`.length;
}

/** Whether a file needs the compression pipeline before embedding.
 *  SVG is ALWAYS transcoded to raster regardless of size: `MarkdownContent`'s
 *  urlTransform (frontend-doctrine §14.4) rejects `data:image/svg+xml` for
 *  script-carrying risk, so an untranscoded SVG embed would silently render
 *  blank in the read view. */
export function shouldCompressImage(
  file: { type: string; size: number },
  budgetBytes: number = IMAGE_COMPRESSION.defaultBudgetBytes,
): boolean {
  if (file.type === "image/svg+xml") return true;
  return estimateDataUrlBytes(file.size, file.type) > budgetBytes;
}

/** Scale (never upscale) natural dimensions to fit a square bound,
 *  preserving aspect ratio; results are integer pixels, floored at 1. */
export function fitImageDimensions(
  width: number,
  height: number,
  maxDimensionPx: number,
): { width: number; height: number } {
  const scale = Math.min(1, maxDimensionPx / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
